import { userManager } from '@/Core/Managers/User'

/**
 * Snapshot of dispatcher / admin / senior-dispatcher capabilities over a single executor.
 * Computed from the current `userManager.user` + the executor's owning dispatcher.
 *
 * The shape mirrors backend ACL (TransportRequestRule / ExecutorAssignmentRule), so a UI
 * decision matches what the API would allow. When the UI shows a button that the backend
 * forbids — fix this helper first.
 */
export interface RequestExecutorPermissions {
    isCurrentDispatcher: boolean
    isAdmin: boolean
    isSeniorDispatcher: boolean

    /** "Взяти в роботу" available — requires executor.status === 'new'. */
    canTakeInWork: boolean
    /** "Транспорт розподілено" available — requires executor.status === 'in_work' and !isTransportDistributed. */
    canMarkTransportDistributed: boolean
    /** Senior dispatcher or admin only — regular dispatcher would get 403 from backend. */
    canDelegate: boolean
    /** Sending push messages to drivers under this executor. */
    canSendMessage: boolean
    /** Manage assignment-level actions (status change, push, confirm) in the activeTask bar. */
    canManageTasks: boolean
    /** Edit assignment cells (carrier / driver / vehicle / weight / trips). */
    canEdit: boolean
}

const hasRole = (...roles: string[]): boolean => {
    const userRoles = userManager.user?.['roles'] as string[] | undefined
    if (!userRoles) return false
    return userRoles.some(r => roles.includes(r))
}

/**
 * Permission flags MUST mirror the backend Voters after the role refactor:
 *   - TransportRequestRule: take_in_work / delegate / cancel — see canDelegate below.
 *   - ExecutorAssignmentRule: update / change_status / confirm_execution — granted to
 *     ROLE_ADMIN | ROLE_DISPATCHER | ROLE_SENIOR_DISPATCHER for ANY assignment
 *     (no "owning dispatcher" restriction on the backend).
 * `isCurrentDispatcher` is kept for informational UI only (e.g. highlighting), NOT for
 * gating actions — gating must match the backend, which allows any dispatcher.
 */
export const getRequestExecutorPermissions = (executor: any): RequestExecutorPermissions => {
    const currentUuid = userManager.user?.['uuid']
    const isCurrentDispatcher = !!(currentUuid && executor?.dispatcher?.uuid && currentUuid === executor.dispatcher.uuid)
    const isAdmin = hasRole('ROLE_ADMIN', 'ROLE_SUPER_ADMIN')
    const isSeniorDispatcher = hasRole('ROLE_SENIOR_DISPATCHER')

    // Any dispatcher-class role (regular dispatcher, senior, admin) — matches the
    // backend, which does not restrict these actions to the owning dispatcher.
    const anyDispatcher = hasRole('ROLE_DISPATCHER', 'ROLE_SENIOR_DISPATCHER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN')

    return {
        isCurrentDispatcher,
        isAdmin,
        isSeniorDispatcher,
        canTakeInWork: executor?.status === 'new' && anyDispatcher,
        canMarkTransportDistributed: executor?.status === 'in_work' && !executor?.isTransportDistributed && anyDispatcher,
        canDelegate: !!executor?.dispatcher && (isSeniorDispatcher || isAdmin),
        canSendMessage: anyDispatcher,
        canManageTasks: anyDispatcher,
        canEdit: anyDispatcher,
    }
}

/**
 * Per-assignment delete permission — mirrors ExecutorAssignmentRule::canDelete:
 * - Admin can delete any assignment.
 * - Any dispatcher (regular or senior) can delete only assignments still in
 *   `assigned_executor` state.
 */
export const canDeleteAssignment = (
    permissions: RequestExecutorPermissions,
    assignmentStatus: string | null | undefined,
): boolean => {
    if (permissions.isAdmin) return true
    const isDispatcher = hasRole('ROLE_DISPATCHER', 'ROLE_SENIOR_DISPATCHER')
    if (isDispatcher && assignmentStatus === 'assigned_executor') return true
    return false
}
